(function () {
    // Formula based off excel doc
    // https://intranet.americaneagle.com/tickets/view.asp?MODE=VIEW&TICKET_ID=705071&

    class Zone {
        constructor(options) {
            this.options = options;
            this.name = this.options.zone;
            this.q = (this.options.deltaU * this.options.openingSize * this.options.dt).toFixed(2);
            this.energySavings = Math.round(this.options.time * this.q);
            this.energySavingsSecondary = (this.energySavings / 3412).toFixed(2);
            this.adjustedEnergySavings = (this.energySavingsSecondary / this.options.efficiencyRating).toFixed(2);
            this.savings = (this.adjustedEnergySavings * this.options.costOfElectricity).toFixed(2);
            this.paybackPeriod = ((this.options.thermiserMaxLowUPrice - this.options.thermiserMaxPrice) / this.savings).toFixed(2);
        }
    }

    class CostCalculator {
        constructor(element) {
            this.app = element;
            this.uFactor1 = 0.82;
            this.uFactor2 = 0.53;
            this.inputErrorCount = 0;
            this.doorSize = {
                "widthFt": '',
                "widthIn": '',
                "heightFt": '',
                "heightIn": '',
            };
            this.options = {
                deltaU: this.uFactor1 - this.uFactor2,
                efficiencyRating: .9,
                costOfElectricity: 0.155,
                thermiserMaxPrice: 5746.00,
                thermiserMaxLowUPrice: 6200.00,
                openingSize: 0,
                time: 365 * 24,
            };
            this.headings = [
                'Climate Zone',
                'Annual Savings per Door',
            ];
            this.zones = [
                {
                    zone: "Zone 1 (Hot)",
                    dt: 20,
                },
                {
                    zone: "Zone 2 (Hot-Humid)",
                    dt: 23.30,
                },
                {
                    zone: "Zone 3 (Warm)",
                    dt: 32.50,
                },
                {
                    zone: "Zone 4 (Mixed)",
                    dt: 37.50,
                },
                {
                    zone: "Zone 5 (Cool)",
                    dt: 45.00,
                },
                {
                    zone: "Zone 6 (Cold)",
                    dt: 56.25,
                },
                {
                    zone: "Zone 7 (Very Cold)",
                    dt: 73.00,
                },
                {
                    zone: "Zone 8 (Subarctic/Arctic)",
                    dt: 81.50,
                },
            ];
            this.singleComponents = {
                submit: '[cost-calculator-submit]',
                resultsContainer: '[cost-calculator-results]',
                notificationContainer: '[cost-calculator-notification]',
                formError: '[cost-calculator-form-error]',
                doorSelect: '[cost-calculator-door]',
            };
            this.multiComponents = {
                inputs: '[cost-calculator-input]',
                errors: '[cost-calculator-error]',
            };
            this.setUpComponents();
            this.eventHandlers();
        }
        
        setUpComponents() {
            for (const [key,value] of Object.entries(this.singleComponents)) {
                this[key] = this.app.querySelector(value);
            }

            for (const [key,value] of Object.entries(this.multiComponents)) {
                this[key] = this.app.querySelectorAll(value);
            }
        }

        eventHandlers() {
            this.submit?.addEventListener('click', () => this.calculateResults());

            this.inputs.forEach((input) => {
                input.addEventListener('input', (input) => this.onInputChange(input));
            });

            this.doorSelect?.addEventListener('change', (e) => this.onDoorSelectChange(e));
        }

        onDoorSelectChange(e) {
            const target = e.target;
            const value = target.value;
            this.uFactor1 = Number(value);
            this.options.deltaU = this.uFactor1 - this.uFactor2;
        }

        onInputChange(input) {
            const target = input.target;
            const name = target.name;
            const value = target.value;
            const isValid = this.validateInput(target);
            if (isValid) {
                if (value === '') {
                    this.doorSize[name] = 0;
                } else {
                    this.doorSize[name] = value;
                }
                this.calculateOpeningSize();
            }
        }
        
        validateInput(inputField) {
            const isNumberRegex =/^[0-9]+$/;
            const value = inputField.value;
            const isNumber = value.match(isNumberRegex);
            const error = inputField.nextElementSibling;
            if (isNumber || value === '') {
                if (error.hasAttribute('cost-calculator-error')) {
                    error.classList.remove('active');
                    error.innerHTML = '';
                }
                return true;
            }
            error.classList.add('active');
            error.textContent = `'${value}' is not a number`;
            return false;
        }

        validateForm() {
            // since validation is done on input change
            // if any errors are visible, it means the form is not valid
            let isValid = true;
            this.errors.forEach((item) => {
                if (item.classList.contains('active')) {
                    isValid = false;
                }
            });
            // at least one width and height value must be entered
            let hasHeight = false;
            let hasWidth = false;
            for (const [key,value] of Object.entries(this.doorSize)) {
                if (value !== '' && value > 0) {
                    if (key === 'widthFt' || key === 'widthIn') {
                        hasWidth = true;
                    }

                    if (key === 'heightFt' || key === 'heightIn') {
                        hasHeight = true;
                    }
                }
            }
            if (!hasHeight || !hasWidth) {
                this.formError.innerHTML = '';
                this.formError.textContent = `At least one height and width value is required`;
                isValid = false;
            }
            return isValid;
        }

        calculateOpeningSize() {
            const width = Number(this.doorSize['widthFt']) + (Number(this.doorSize['widthIn']) / 12);
            const height = Number(this.doorSize['heightFt']) + (Number(this.doorSize['heightIn']) / 12);
            this.options.openingSize = width * height;
        }

        calculateResults() {
            const formIsValid = this.validateForm();
            this.notificationContainer.innerHTML = '';
            if (formIsValid) {
                this.formError.innerHTML = '';
                this.notificationContainer.textContent = `Cost calculator table results updated`;
                const results = `
                    <div class="table-wrapper" tabindex="0" role="region" aria-label="Cost calculator results">
                        <table class="calculator-table">
                            <thead>
                                <tr class="calculator-table__tr">
                                    ${this.headings.map((heading) => {
                                        return `<th class="calculator-table__th">${heading}</th>`;
                                    }).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                ${this.zones.map((zoneOptions) =>{
                                    const options = {
                                        ...this.options,
                                        ...zoneOptions
                                    };
                                    const zone = new Zone(options);
                                    return `
                                        <tr class="calculator-table__tr">
                                            <th scope="row" class="calculator-table__td">${zone.name}</th>
                                            <td class="calculator-table__td">${zone.savings}</td>
                                        </tr>
                                    `
                                }).join('')}
                            </tbody>
                        </table>
                    </div>
                `;
                this.resultsContainer.innerHTML = '';
                this.resultsContainer.insertAdjacentHTML('beforeend', results);
            } else {
                this.notificationContainer.textContent = `There are errors in the cost calculator form`;
            }
        }
    }

    const calculator = document.querySelector('[cost-calculator]');
    if (calculator) {
        new CostCalculator(calculator);
    }
})();